
import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Language, LANGUAGE_NAMES } from '../translations';
import { Expedition } from '../types';
import { getLocalizedPath, getCleanPath, formatDuration } from '../lib/i18n';

interface NavbarProps {
    lang: Language;
    setLang: (l: Language) => void;
    langOpen: boolean;
    setLangOpen: (o: boolean) => void;
    langRef: React.RefObject<HTMLDivElement | null>;
    t: any;
    LANGUAGES: Language[];
    expeditions: Expedition[];
    getExpField: <T>(exp: any, field: string, lang: Language) => T;
}

const Navbar: React.FC<NavbarProps> = ({ lang, setLang, langOpen, setLangOpen, langRef, t, LANGUAGES, expeditions, getExpField }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const cleanPath = getCleanPath(location.pathname);
    const isHome = cleanPath === '/' || cleanPath === '/homea' || cleanPath === '/homeb';
    const [megaMenuOpen, setMegaMenuOpen] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    // Track scroll to change navbar style
    React.useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 100);
        };
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Prevent scroll when mobile menu is open
    React.useEffect(() => {
        if (mobileMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
    }, [mobileMenuOpen]);

    // Close menus on route change
    React.useEffect(() => {
        setMegaMenuOpen(false);
        setMobileMenuOpen(false);
    }, [location]);

    const categories = [
        { id: 'trekking', name: t.cat_trekking },
        { id: 'climbing', name: t.cat_climbing },
        { id: 'water', name: t.cat_water },
    ];

    // Style helper
    const isTransparent = isHome && !megaMenuOpen && !scrolled && !mobileMenuOpen;

    const handleSelectLanguage = (l: Language) => {
        setLang(l);
        setLangOpen(false);
        const newPath = getLocalizedPath(location.pathname + location.search, l);
        navigate(newPath);
    };

    return (
        <>
            <nav
                className={`fixed top-0 w-full z-[60] transition-all duration-500 flex flex-col items-center ${isTransparent
                    ? 'bg-gradient-to-b from-black/60 to-transparent py-0 md:py-5'
                    : 'bg-[#1a1512]/95 backdrop-blur-md border-b border-rock py-0 md:py-3'
                    }`}
                onMouseLeave={() => setMegaMenuOpen(false)}
            >
                {/* SERNATUR TOP BADGE - Mobile Only (links to Certificaciones page) */}
                <Link
                    to={getLocalizedPath('/certificaciones', lang)}
                    onClick={() => {
                        console.log("🔍 Sernatur mobile top badge clicked! Navigating to /certificaciones");
                        if (typeof window.gtag === 'function') {
                            window.gtag('event', 'click_sernatur', {
                                location: 'navbar_mobile'
                            });
                        }
                    }}
                    title="Ver Certificaciones y Registro SERNATUR"
                    className="md:hidden w-full bg-black/40 border-b border-white/5 py-2 px-4 flex justify-center items-center gap-3 group"
                >
                    <img
                        src="/sernatur/logo-sernatur-sin-fecha-transparent.png"
                        alt="SERNATUR - Servicio Turístico Registrado"
                        className="h-[42px] rounded-full group-hover:scale-105 transition-transform"
                    />
                    <div className="flex flex-col text-left">
                        <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/80 group-hover:text-[#00d4ff] transition-colors">
                            {t.sernatur_accredited || (lang === 'en' ? 'Certified' : 'Acreditados')}
                        </span>
                        <span className="text-[8px] uppercase tracking-[0.2em] text-white/50 group-hover:text-white/80 transition-colors">
                            {t.sernatur_guide || (lang === 'en' ? 'Sernatur Guide' : 'Sernatur')}
                        </span>
                    </div>
                </Link>

                <div className="max-w-[1440px] mx-auto px-4 md:px-6 w-full flex items-center justify-between py-2.5 md:py-0">
                    <Link
                        to={getLocalizedPath('/', lang)}
                        className="flex items-center gap-2 cursor-pointer group z-[70] min-w-0"
                        onClick={() => setMobileMenuOpen(false)}
                    >
                        {/* Logo Fisura Expeditions */}
                        <div className="relative h-[44px] md:h-[52px] flex items-center">
                            <img
                                src="/logo-fisura-white.png"
                                className={`transition-all duration-500 w-auto object-contain ${
                                    isTransparent 
                                        ? 'h-[42px] sm:h-[48px] md:h-[52px]' 
                                        : 'h-[34px] sm:h-[38px] md:h-[42px]'
                                }`}
                                style={{ filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.85))' }}
                                alt="Fisura Expeditions"
                            />
                        </div>
                    </Link>

                    {/* DESKTOP NAV */}
                    <div className="hidden md:flex items-center space-x-5 lg:space-x-7 xl:space-x-9 uppercase text-[11px] xl:text-[12px] tracking-[0.14em] xl:tracking-[0.18em] font-bold">
                        {/* EXPLORE ROUTES WITH MEGA MENU TRIGGER */}
                        <div
                            className="relative h-full flex items-center cursor-pointer py-2"
                            onMouseEnter={() => setMegaMenuOpen(true)}
                        >
                            <Link
                                to={getLocalizedPath('/expeditions', lang)}
                                className={`hover:text-[#00d4ff] transition-colors flex items-center gap-1 text-shadow ${cleanPath.startsWith('/expeditions') && !location.search.includes('wellness') ? 'text-[#00d4ff]' : ''}`}
                            >
                                {t.nav_expeditions} <span className={`text-[8px] transition-transform duration-300 ${megaMenuOpen ? 'rotate-180' : ''}`}>▾</span>
                            </Link>
                        </div>

                        <Link
                            to={getLocalizedPath('/expeditions/wellness-day', lang)}
                            className={`hover:text-[#00d4ff] transition-colors text-shadow ${cleanPath === '/expeditions/wellness-day' ? 'text-[#00d4ff]' : ''}`}
                        >
                            {t.cat_wellness}
                        </Link>

                        <Link to={getLocalizedPath('/about', lang)} className={`hover:text-[#00d4ff] transition-colors text-shadow ${cleanPath === '/about' ? 'text-[#00d4ff]' : ''}`}>{t.nav_about}</Link>
                        <Link to={getLocalizedPath('/certificaciones', lang)} className={`hover:text-[#00d4ff] transition-colors text-shadow ${['/certificaciones', '/certifications', '/certificacoes', '/sustentabilidad', '/sustainability', '/compromiso-sustentable'].includes(cleanPath) ? 'text-[#00d4ff]' : ''}`}>{t.nav_certifications || 'Certificaciones'}</Link>
                        <Link to={getLocalizedPath('/blog', lang)} className={`hover:text-[#00d4ff] transition-colors text-shadow ${cleanPath.startsWith('/blog') ? 'text-[#00d4ff]' : ''}`}>{t.nav_blog || 'Blog'}</Link>
                    </div>

                    <div className="flex items-center space-x-2 md:space-x-3 lg:space-x-4 xl:space-x-6 shrink-0">

                        {/* SERNATUR & CERTIFICATIONS BADGE - Desktop only */}
                        <Link
                            to={getLocalizedPath('/certificaciones', lang)}
                            onClick={() => {
                                if (typeof window.gtag === 'function') {
                                    window.gtag('event', 'click_certificaciones_badge', {
                                        location: 'navbar_desktop'
                                    });
                                }
                            }}
                            title="Ver Certificaciones y Registro SERNATUR"
                            className={`hidden md:flex items-center gap-2 xl:gap-2.5 border-l border-white/20 pl-3 xl:pl-4 group transition-all ${
                                ['/certificaciones', '/certifications', '/certificacoes', '/sustentabilidad', '/sustainability', '/compromiso-sustentable'].includes(cleanPath) ? 'text-[#00d4ff]' : ''
                            }`}
                        >
                            <img
                                src="/sernatur/logo-sernatur-sin-fecha-transparent.png"
                                alt="SERNATUR - Servicio Turístico Registrado"
                                className={`transition-all duration-500 rounded-full ${
                                    isTransparent ? 'h-[72px] md:h-[80px]' : 'h-[52px] md:h-[60px]'
                                } opacity-85 group-hover:opacity-100 group-hover:scale-105`}
                            />
                            <div className="flex flex-col text-left">
                                <span className={`text-[9px] font-bold uppercase tracking-[0.25em] transition-colors ${
                                    ['/certificaciones', '/certifications', '/certificacoes', '/sustentabilidad', '/sustainability', '/compromiso-sustentable'].includes(cleanPath) ? 'text-[#00d4ff]' : 'text-white/80 group-hover:text-[#00d4ff]'
                                }`}>
                                    {t.sernatur_accredited || (lang === 'en' ? 'Certified' : 'Acreditados')}
                                </span>
                                <span className="text-[8px] uppercase tracking-[0.2em] text-white/50 group-hover:text-white/80 transition-colors">
                                    {t.sernatur_guide || (lang === 'en' ? 'Sernatur Guide' : 'Sernatur')}
                                </span>
                            </div>
                        </Link>

                        <div className="relative" ref={langRef}>
                            <button
                                onClick={() => setLangOpen(!langOpen)}
                                className="border border-rock px-2.5 py-1 md:px-3 md:py-0.5 text-[11px] md:text-[10px] font-bold hover:bg-[#f2f2f2] hover:text-[#0a0a0a] transition-all flex items-center gap-1 uppercase tracking-widest"
                            >
                                {lang} <span className="text-[9px] md:text-[8px]">▾</span>
                            </button>
                            {langOpen && (
                                <div className="absolute right-0 top-8 bg-[#0a0a0a] border border-rock z-50 min-w-[130px] shadow-2xl">
                                    {LANGUAGES.map(l => (
                                        <button
                                            key={l}
                                            onClick={() => handleSelectLanguage(l)}
                                            className={`w-full text-left px-4 py-2.5 text-[10px] font-bold uppercase tracking-widest hover:bg-[#00d4ff] hover:text-black transition-colors flex items-center gap-2 ${lang === l ? 'text-[#00d4ff]' : 'text-gray-400'}`}
                                        >
                                            <span className="text-[8px] font-mono">{l.toUpperCase()}</span>
                                            <span>{LANGUAGE_NAMES[l]}</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* BOOK BUTTON - HIDDEN ON MOBILE */}
                        <Link
                            to={getLocalizedPath('/expeditions', lang)}
                            className={`hidden md:block font-title text-base xl:text-lg px-4 xl:px-6 py-1.5 transition-all uppercase tracking-widest ${isTransparent
                                ? 'border border-white/30 hover:border-white text-white hover:bg-white/10 backdrop-blur-sm'
                                : 'bg-[#f2f2f2] text-black hover:bg-[#00d4ff]'
                                } ${isTransparent ? 'text-shadow' : ''}`}
                        >
                            {t.nav_book}
                        </Link>

                        {/* HAMBURGER MENU BUTTON */}
                        <button
                            className="md:hidden flex flex-col space-y-1.5 z-[70] p-1.5 shrink-0"
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                            aria-label="Toggle Menu"
                        >
                            <span className={`w-6 h-[2.5px] bg-white transition-all transform ${mobileMenuOpen ? 'rotate-45 translate-y-2' : ''}`} />
                            <span className={`w-6 h-[2.5px] bg-white transition-all ${mobileMenuOpen ? 'opacity-0' : ''}`} />
                            <span className={`w-6 h-[2.5px] bg-white transition-all transform ${mobileMenuOpen ? '-rotate-45 -translate-y-2' : ''}`} />
                        </button>
                    </div>
                </div>

                {/* MEGA MENU CONTENT (DESKTOP) */}
                <div
                    className={`hidden md:block w-full overflow-hidden transition-all duration-500 ease-in-out bg-[#1a1512] border-t border-rock ${megaMenuOpen ? 'max-h-[600px] opacity-100 mt-4' : 'max-h-0 opacity-0'}`}
                    onMouseEnter={() => setMegaMenuOpen(true)}
                >
                    <div className="max-w-7xl mx-auto px-6 py-12 flex flex-wrap justify-center gap-12 md:gap-24">
                        {categories.map(cat => (
                            <div key={cat.id} className="space-y-6 w-full md:w-64">

                                <h3 className="text-[#00d4ff] font-title text-2xl tracking-widest border-b border-white/10 pb-2 uppercase">
                                    {cat.name}
                                </h3>
                                <ul className="space-y-3">
                                    {expeditions.filter(e => e.category === cat.id && e.is_visible !== false).map(exp => {
                                        const localizedDuration = formatDuration(getExpField<string>(exp, 'duration', lang) || exp.duration, lang);
                                        return (
                                            <li key={exp.id}>
                                                <Link
                                                    to={getLocalizedPath(`/expeditions/${exp.slug}`, lang)}
                                                    className="group flex flex-col space-y-1 hover:pl-2 transition-all duration-300"
                                                    onClick={() => setMegaMenuOpen(false)}
                                                >
                                                    <span className="text-sm font-bold tracking-tight group-hover:text-[#00d4ff]">
                                                        {getExpField<string>(exp, 'name', lang)}
                                                    </span>
                                                    <span className="text-[10px] text-gray-500 uppercase tracking-widest">
                                                        {localizedDuration}
                                                    </span>
                                                </Link>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        ))}
                    </div>

                    <div className="bg-black/30 py-4">
                        <div className="max-w-7xl mx-auto px-6 flex justify-end">
                            <Link
                                to={getLocalizedPath('/expeditions', lang)}
                                className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#00d4ff] hover:text-white transition-colors"
                                onClick={() => setMegaMenuOpen(false)}
                            >
                                {t.featured_cta} →
                            </Link>
                        </div>
                    </div>
                </div>
            </nav>

            {/* MOBILE MENU OVERLAY */}
            <div className={`fixed inset-0 bg-stone z-[55] transition-transform duration-700 ease-[cubic-bezier(0.23,1,0.32,1)] md:hidden ${mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}>
                <div className="h-full flex flex-col pt-32 px-10 pb-12 overflow-y-auto">
                    <div className="space-y-10 text-white">
                        <div className="space-y-6">
                            <span className="text-[10px] font-bold uppercase tracking-[0.4em] text-[#00d4ff]">{t.nav_home}</span>
                            <div className="flex flex-col space-y-4 text-3xl font-title uppercase tracking-tighter">
                                <Link to={getLocalizedPath('/', lang)} onClick={() => setMobileMenuOpen(false)} className={`hover:text-[#00d4ff] ${cleanPath === '/' ? 'text-[#00d4ff]' : ''}`}>{t.nav_home}</Link>
                                <Link to={getLocalizedPath('/expeditions', lang)} onClick={() => setMobileMenuOpen(false)} className={`hover:text-[#00d4ff] ${cleanPath === '/expeditions' ? 'text-[#00d4ff]' : ''}`}>{t.nav_expeditions}</Link>
                                <Link to={getLocalizedPath('/expeditions/wellness-day', lang)} onClick={() => setMobileMenuOpen(false)} className={`hover:text-[#00d4ff] ${cleanPath === '/expeditions/wellness-day' ? 'text-[#00d4ff]' : ''}`}>{t.cat_wellness}</Link>
                                <Link to={getLocalizedPath('/about', lang)} onClick={() => setMobileMenuOpen(false)} className={`hover:text-[#00d4ff] ${cleanPath === '/about' ? 'text-[#00d4ff]' : ''}`}>{t.nav_about}</Link>
                                <Link to={getLocalizedPath('/certificaciones', lang)} onClick={() => setMobileMenuOpen(false)} className={`hover:text-[#00d4ff] ${['/certificaciones', '/certifications', '/certificacoes', '/sustentabilidad', '/sustainability', '/compromiso-sustentable'].includes(cleanPath) ? 'text-[#00d4ff]' : ''}`}>{t.nav_certifications || 'Certificaciones'}</Link>
                                <Link to={getLocalizedPath('/blog', lang)} onClick={() => setMobileMenuOpen(false)} className={`hover:text-[#00d4ff] ${cleanPath.startsWith('/blog') ? 'text-[#00d4ff]' : ''}`}>{t.nav_blog || 'Blog'}</Link>
                            </div>
                        </div>

                        <div className="space-y-8 pt-6 border-t border-white/10">
                            {categories.map(cat => (
                                <div key={cat.id} className="space-y-4">
                                    <h3 className="text-[#00d4ff] font-title text-xl tracking-[0.2em] uppercase">{cat.name}</h3>
                                    <div className="flex flex-col space-y-3">
                                        {expeditions.filter(e => e.category === cat.id && e.is_visible !== false).map(exp => {
                                            const localizedDuration = formatDuration(getExpField<string>(exp, 'duration', lang) || exp.duration, lang);
                                            return (
                                                <Link
                                                    key={exp.id}
                                                    to={getLocalizedPath(`/expeditions/${exp.slug}`, lang)}
                                                    className="group flex flex-col"
                                                    onClick={() => setMobileMenuOpen(false)}
                                                >
                                                    <span className="text-lg font-bold tracking-tight">{getExpField<string>(exp, 'name', lang)}</span>
                                                    <span className="text-[9px] text-gray-500 uppercase tracking-widest">{localizedDuration}</span>
                                                </Link>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="mt-auto pt-16 flex flex-col gap-6">
                        {/* SERNATUR BADGE - Mobile Menu Drawer */}
                        <Link
                            to={getLocalizedPath('/certificaciones', lang)}
                            className="flex items-center gap-3.5 border border-white/10 rounded px-4 py-3 bg-white/5 hover:bg-white/10 transition-colors w-full justify-center group"
                            onClick={() => {
                                setMobileMenuOpen(false);
                                if (typeof window.gtag === 'function') {
                                    window.gtag('event', 'click_sernatur', {
                                        location: 'navbar_mobile_drawer'
                                    });
                                }
                            }}
                        >
                            <img
                                src="/sernatur/logo-sernatur-sin-fecha-transparent.png"
                                alt="SERNATUR - Servicio Turístico Registrado"
                                className="h-[48px] rounded-full group-hover:scale-105 transition-transform"
                            />
                            <div className="flex flex-col text-left">
                                <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/80 group-hover:text-[#00d4ff] transition-colors">
                                    {t.sernatur_accredited ? `${t.sernatur_accredited} Sernatur` : (lang === 'en' ? 'Certified Sernatur' : 'Acreditados Sernatur')}
                                </span>
                                <span className="text-[8px] uppercase tracking-widest text-white/40">
                                    {lang === 'en' ? 'Official Mountain Certifications' : 'Ver Certificaciones Oficiales'}
                                </span>
                            </div>
                        </Link>

                        <Link
                            to={getLocalizedPath('/expeditions', lang)}
                            onClick={() => setMobileMenuOpen(false)}
                            className="block w-full bg-[#00d4ff] text-black text-center font-title text-xl py-4 uppercase tracking-[0.2em]"
                        >
                            {t.nav_book}
                        </Link>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Navbar;
